import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { LegalLinks } from "@/components/legal/legal-links";

/**
 * The order step: everything a buyer must read before committing to pay
 * (CK 6.228¹¹), and the button that commits them.
 *
 * The button reads „Užsakyti su pareiga mokėti" because the law asks the
 * order-placing button to say so, and Stripe's own button cannot be relabelled
 * — so the order is placed here and Stripe's page repeats the sentence above its
 * pay button (`Billing::Checkout::SUBMIT_MESSAGES`).
 *
 * Where the offer carries a consent text the button stays disabled until its
 * checkbox is ticked — never pre-ticked, and reset whenever the dialog opens on
 * another plan. The server refuses the order without it either way
 * (`Billing::Consent`); this only keeps the button honest.
 */

export type Offer = {
  plan: string;
  label: string;
  mode: string;
  interval: string | null;
  amountCents: number | null;
  currency: string | null;
  credits: number;
  consentText: string | null;
  consentVersion: string | null;
};

export type CreditCost = { action: string; label: string; credits: number };

export const ORDER_BUTTON_LABEL = "Užsakyti su pareiga mokėti";

export function formatPrice(offer: Pick<Offer, "amountCents" | "currency">): string | null {
  if (offer.amountCents == null || !offer.currency) return null;
  try {
    return new Intl.NumberFormat("lt-LT", {
      style: "currency",
      currency: offer.currency.toUpperCase(),
    }).format(offer.amountCents / 100);
  } catch {
    return null;
  }
}

/** 1 kreditas, 2 kreditai, 10 kreditų, 21 kreditas, 12 kreditų. */
export function creditsWord(n: number): string {
  const lastTwo = n % 100;
  const last = n % 10;
  if (lastTwo >= 11 && lastTwo <= 19) return "kreditų";
  if (last === 1) return "kreditas";
  if (last >= 2) return "kreditai";
  return "kreditų";
}

const PER: Record<string, string> = { month: "per mėnesį", year: "per metus" };
const RENEWS: Record<string, string> = { month: "kas mėnesį", year: "kas metus" };

export function OrderDialog({
  offer,
  creditCosts,
  pending,
  onOpenChange,
  onConfirm,
}: {
  offer: Offer | null;
  creditCosts: CreditCost[];
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (offer: Offer) => void;
}) {
  const subscription = offer?.mode === "subscription";
  const price = offer ? formatPrice(offer) : null;
  const interval = offer?.interval ?? "month";
  const [agreed, setAgreed] = useState(false);
  useEffect(() => setAgreed(false), [offer?.plan]);
  const needsConsent = Boolean(offer?.consentText);

  return (
    <Dialog open={offer !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {offer && (
          <>
            <DialogHeader>
              <DialogTitle>
                {subscription ? `Scio prenumerata — ${offer.label.toLowerCase()}` : `AI kreditai: ${offer.label}`}
              </DialogTitle>
              <DialogDescription>Prieš užsakydami perskaitykite pagrindines sąlygas.</DialogDescription>
            </DialogHeader>

            <dl className="space-y-3 text-sm">
              <div>
                <dt className="font-medium">Kaina</dt>
                <dd className="text-muted-foreground">
                  {price ? (
                    <>
                      <strong className="text-foreground">{price}</strong>
                      {subscription && ` ${PER[interval] ?? ""}`}, su PVM.
                    </>
                  ) : (
                    "Tiksli kaina su PVM bus parodyta „Stripe“ mokėjimo puslapyje prieš mokant."
                  )}
                  {!subscription && " Vienkartinis mokėjimas — niekas neatsinaujina."}
                </dd>
              </div>

              {subscription && (
                <>
                  <div>
                    <dt className="font-medium">Automatinis atsinaujinimas</dt>
                    <dd className="text-muted-foreground">
                      Prenumerata atsinaujina automatiškai {RENEWS[interval] ?? "kiekvieną laikotarpį"}, o
                      mokestis nuskaitomas tuo pačiu mokėjimo būdu, kol prenumeratos neatšauksite.
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium">Kaip nutraukti</dt>
                    <dd className="text-muted-foreground">
                      Bet kada šiame puslapyje („Prenumerata ir kreditai“) mygtuku „Atšaukti
                      prenumeratą“. Apmokėtas laikotarpis galioja iki pabaigos, kitas mokestis
                      nebenuskaitomas.
                    </dd>
                  </div>
                </>
              )}

              <div>
                <dt className="font-medium">AI kreditai</dt>
                <dd className="space-y-2 text-muted-foreground">
                  <p>
                    AI kreditai — tai vienetai, kuriuos naudoja AI funkcijos. Užduotys, teorija ir
                    sąvokų kortelės kreditų nenaudoja.{" "}
                    {subscription ? (
                      <>
                        Prenumerata kiekvieną mėnesį atnaujina balansą iki{" "}
                        <strong className="text-foreground">{offer.credits}</strong> kreditų.
                      </>
                    ) : (
                      <>
                        Iškart po apmokėjimo į balansą bus įskaityta{" "}
                        <strong className="text-foreground">{offer.credits}</strong> kreditų.
                        Nepanaudoti papildymo kreditai lieka ir kitą mėnesį.
                      </>
                    )}
                  </p>
                  {creditCosts.length > 0 && (
                    <ul className="list-disc space-y-0.5 pl-5">
                      {creditCosts.map((cost) => (
                        <li key={cost.action}>
                          {cost.label} — {cost.credits} {creditsWord(cost.credits)}
                        </li>
                      ))}
                    </ul>
                  )}
                </dd>
              </div>
            </dl>

            {needsConsent && (
              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm">
                <Checkbox
                  checked={agreed}
                  onCheckedChange={(value) => setAgreed(value === true)}
                  className="mt-0.5"
                  aria-label="Sutikimas"
                />
                <span>{offer.consentText}</span>
              </label>
            )}

            <p className="text-xs text-muted-foreground">
              Mokėsite saugiame „Stripe“ puslapyje. Visos sąlygos —{" "}
              <LegalLinks className="ml-1" />
            </p>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
                Atšaukti
              </Button>
              <Button onClick={() => onConfirm(offer)} disabled={pending || (needsConsent && !agreed)}>
                {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {ORDER_BUTTON_LABEL}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
